import {z} from "zod";

export const catalogId = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, "Enter a valid UUID");
export const catalogSearchValidator = z.object({
    companyId: catalogId.optional(),
    dataSourceTemplateId: catalogId.optional(),
    dataClusterTemplateId: catalogId.optional(),
    search: z.string().max(200).catch("").default(""),
    status: z.string().max(50).catch("").default(""),
    page: z.coerce.number().int().min(1).max(1000000).catch(1).default(1),
    tab: z.enum(["overview", "accounts", "templates"]).catch("overview").default("overview"),
    accountSearch: z.string().max(200).catch("").default(""),
    accountStatus: z.string().max(50).catch("").default(""),
    accountPage: z.coerce.number().int().min(1).max(1000000).catch(1).default(1),
});
export type CatalogSearch = z.infer<typeof catalogSearchValidator>;
export type Company = {id: string; displayName: string};
export function clusterLinkSearch(companyId: string | undefined, dataSourceTemplateId: string, dataClusterTemplateId: string) {
    return {companyId, dataSourceTemplateId, dataClusterTemplateId};
}
export async function loadAllCompanies<T extends Company>(fetchPage: (offset: number, limit: number) => Promise<T[]>, limit = 500): Promise<T[]> {
    const rows: T[] = [];
    for (let offset = 0; ; offset += limit) {
        const page = await fetchPage(offset, limit);
        rows.push(...page);
        if (page.length < limit) {
            return rows;
        }
    }
}
const credential = z.object({
    attributeName: z
        .string()
        .trim()
        .regex(/^[A-Za-z_][A-Za-z0-9_]*$/, "Use letters, digits and underscores; start with a letter or underscore"),
    displayName: z.string().trim().min(1),
    attributeType: z.literal("string"),
    nullable: z.boolean(),
    description: z.string().default(""),
});
export const createSourceValidator = z
    .object({
        displayName: z.string().trim().min(1, "Name is required").max(200),
        description: z.string().max(4000),
        tags: z.array(z.string().trim().min(1)),
        connectionStrategy: catalogId,
        imageId: catalogId,
        companies: z.array(catalogId).min(1, "Select at least one company").nullable(),
        sourceCredentialsSchema: z.array(credential),
    })
    .refine((value) => new Set(value.sourceCredentialsSchema.map((field) => field.attributeName)).size === value.sourceCredentialsSchema.length, {
        message: "Credential field names must be unique",
        path: ["sourceCredentialsSchema"],
    });
export type CreateSource = z.infer<typeof createSourceValidator>;
export const sourceValidator = z.object({
    id: catalogId,
    displayName: z.string(),
    description: z.string().nullable(),
    connectionStrategy: catalogId,
    active: z.boolean(),
    tags: z.array(z.string()),
    companies: z.array(catalogId).nullable(),
    accountCount: z.number(),
    templateCount: z.number(),
});
export const templateValidator = z.object({
    id: catalogId,
    displayName: z.string(),
    description: z.string().nullable(),
    active: z.boolean(),
    pipeline: z.string(),
    pipelineBackend: z.string(),
    tableSchema: z.unknown(),
    syncConfig: z.unknown(),
    thresholdDays: z.number().nullable(),
    refreshDuration: z.number().nullable(),
    instanceCount: z.number(),
});
export const accountValidator = z.object({
    id: catalogId,
    companyId: catalogId.nullable(),
    accountId: z.string().nullable(),
    accountName: z.string().nullable(),
    status: z.string(),
    createdAt: z.string().nullable(),
    clusterCount: z.number(),
});
export const instanceValidator = z.object({
    id: catalogId,
    companyId: catalogId,
    dataSourceId: catalogId.nullable(),
    sourceName: z.string().nullable(),
    accountId: z.string().nullable(),
    accountName: z.string().nullable(),
    displayName: z.string(),
    status: z.string(),
    createdAt: z.string().nullable(),
});
export const detailValidator = z.object({
    source: sourceValidator,
    credentialFields: z.array(z.record(z.string(), z.unknown())),
    linkedTemplates: z.array(templateValidator),
});
export const pageOf = <T extends z.ZodType>(item: T) => z.object({items: z.array(item), total: z.number()});
export type Source = z.infer<typeof sourceValidator>;
export type ClusterTemplate = z.infer<typeof templateValidator>;
export type SourceDetail = z.infer<typeof detailValidator>;

export function displayText(value: unknown, fallback = "—"): string {
    return typeof value === "string" && value.trim().length > 0 ? value : fallback;
}

export function tableSummary(schema: unknown): string {
    const columnsText = (columns: unknown[]) => `${columns.length} ${columns.length === 1 ? "column" : "columns"}`;
    if (Array.isArray(schema)) {
        return `1 table · ${columnsText(schema)}`;
    }
    if (schema === null || typeof schema !== "object") {
        return "No table definitions";
    }
    const tables = Object.entries(schema).filter((entry): entry is [string, unknown[]] => Array.isArray(entry[1]));
    return tables.length === 0 ? "No table definitions" : tables.map(([name, columns]) => `${name} · ${columnsText(columns)}`).join(", ");
}
